import React, { useState, useEffect } from 'react';
import { Building2, User, MapPin, Phone, Save, FileText, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Normaliza CUIT a 11 dígitos. Acepta con/sin guiones, espacios o puntos.
const cleanCuit = (s) => (s || '').replace(/\D/g, '').slice(0, 11);
const isValidCuit = (s) => {
  const c = cleanCuit(s);
  if (c.length !== 11) return false;
  // Validación de dígito verificador (Argentina)
  const mults = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(c[i], 10) * mults[i];
  let dv = 11 - (sum % 11);
  if (dv === 11) dv = 0;
  if (dv === 10) return false;
  return dv === parseInt(c[10], 10);
};
// Normaliza WhatsApp a solo dígitos, mínimo 8 (sin código país)
const cleanPhone = (s) => (s || '').replace(/\D/g, '');
const isValidPhone = (s) => cleanPhone(s).length >= 8;

const RegistrationForm = ({ onComplete, previewMode = false }) => {
  const { user, updateCustomerType } = useAuth();
  const { theme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [linkedClient, setLinkedClient] = useState(null);
  const [minOrder, setMinOrder] = useState(0);
  const [previewSubmitted, setPreviewSubmitted] = useState(false);
  const [previewWaLink, setPreviewWaLink] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Arma el link wa.me con el aviso al admin para dar de alta al comercio.
  // Se usa tanto en el flujo real (se abre automáticamente al confirmar)
  // como en el preview del admin (queda visible como link para clickear).
  const buildAdminNotificationWa = async (payload) => {
    let adminWa = '';
    try {
      const ship = await axios.get(`${API}/shipping-settings`);
      adminWa = (ship.data?.whatsappNumber || '').replace(/\D/g, '');
    } catch { /* sin red — sin aviso */ }
    if (!adminWa) return '';
    const lines = [
      `*Nuevo Comercio registrado* 🏪`,
      `Comercio: ${payload.businessName || '-'}`,
      `Responsable: ${`${payload.firstName || ''} ${payload.lastName || ''}`.trim() || '-'}`,
      `WhatsApp: ${payload.phone || '-'}`,
      `CUIT: ${payload.cuit || '-'}`,
      `Condición IVA: ${payload.condicionIva || '-'}`,
      payload.address ? `Dirección: ${payload.address} ${payload.addressNumber || ''}`.trim() : null,
      payload.city ? `Ciudad: ${payload.city}` : null,
      payload.province ? `Provincia: ${payload.province}` : null,
      ``,
      `Revisá y aprobá desde el panel:`,
      `${window.location.origin}/admin/customers`,
    ].filter(Boolean).join('\n');
    return `https://wa.me/${adminWa}?text=${encodeURIComponent(lines)}`;
  };
  const [formData, setFormData] = useState({
    firstName: user?.name?.split(' ')[0] || '',
    lastName: user?.name?.split(' ').slice(1).join(' ') || '',
    cuit: '',
    phone: '',
    address: '',
    addressNumber: '',
    city: '',
    province: 'Córdoba',
    businessName: '',
    customerType: 'comercio',
    condicionIva: 'Responsable Inscripto',
    betweenStreets: '',
    googleMapsLink: '',
  });

  // Devuelve la lista de campos vacíos/inválidos ordenados por aparición
  // en el formulario. Vacío = form OK.
  const getMissingFields = () => {
    const missing = [];
    if (!formData.firstName.trim()) missing.push({ field: 'firstName', label: 'Nombre' });
    if (!formData.lastName.trim()) missing.push({ field: 'lastName', label: 'Apellido' });
    if (formData.customerType === 'comercio') {
      if (!isValidCuit(formData.cuit)) missing.push({ field: 'cuit', label: 'CUIT (11 dígitos válidos)' });
      if (!formData.businessName.trim()) missing.push({ field: 'businessName', label: 'Razón Social / Nombre del Comercio' });
      if (!formData.condicionIva) missing.push({ field: 'condicionIva', label: 'Condición IVA' });
    }
    if (!isValidPhone(formData.phone)) missing.push({ field: 'phone', label: 'WhatsApp (mínimo 8 dígitos)' });
    if (!formData.address.trim()) missing.push({ field: 'address', label: 'Calle' });
    if (!formData.addressNumber.trim()) missing.push({ field: 'addressNumber', label: 'Número de calle' });
    if (!formData.city.trim()) missing.push({ field: 'city', label: 'Ciudad' });
    if (!formData.province.trim()) missing.push({ field: 'province', label: 'Provincia' });
    return missing;
  };
  const missingSet = new Set(getMissingFields().map(m => m.field));
  const hasErr = (field) => attempted && missingSet.has(field);
  const errBorder = (field, base = 'border-slate-300') =>
    hasErr(field) ? 'border-rose-500 ring-2 ring-rose-200' : base;

  useEffect(() => {
    axios.get(`${API}/delivery-cities`)
      .then(r => setCities((r.data.cities || []).map(c => c.toString())))
      .catch(() => setCities([]));
    // Cargar monto mínimo desde configuración de tienda
    axios.get(`${API}/store-settings`)
      .then(r => setMinOrder(Number(r.data?.minimumOrder || 0)))
      .catch(() => {});
  }, []);

  // Buscar coincidencia por CUIT en legacy_customers para sugerir auto-link
  useEffect(() => {
    if (!isValidCuit(formData.cuit)) { setLinkedClient(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/admin/erp/pos/search-customers`, { params: { q: cleanCuit(formData.cuit) } });
        const match = (r.data.items || []).find(c => cleanCuit(c.cuit) === cleanCuit(formData.cuit));
        if (match) {
          setLinkedClient(match);
          setFormData(prev => ({
            ...prev,
            businessName: prev.businessName || match.razon_social || '',
            phone: prev.phone || match.telefono || '',
            address: prev.address || match.direccion || '',
            city: prev.city || match.localidad || '',
            customerType: 'comercio',
          }));
        } else {
          setLinkedClient(null);
        }
      } catch { setLinkedClient(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [formData.cuit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCuitChange = (e) => {
    setFormData(prev => ({ ...prev, cuit: cleanCuit(e.target.value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Marcar que ya intentaron confirmar → mostrar los rojos.
    setAttempted(true);
    const missing = getMissingFields();
    if (missing.length > 0) {
      const first = missing[0];
      setErrorMsg(`Faltan ${missing.length === 1 ? 'este dato' : `${missing.length} datos`}: ${missing.map(m => m.label).join(', ')}.`);
      // Scroll y foco al primer campo faltante (por atributo name)
      try {
        const el = document.querySelector(`[name="${first.field}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => { try { el.focus(); } catch { /* select can't focus */ } }, 300);
        }
      } catch { /* dom race */ }
      return;
    }
    setErrorMsg('');

    // PREVIEW: no persistir nada. Sólo simular el ok visual para que el
    // admin vea cómo se ve el "envío exitoso" del formulario sin tocar
    // su propia sesión ni la DB. También calcula el link wa.me que se
    // enviaría en producción, para que el admin lo pueda testear real.
    if (previewMode) {
      const wa = formData.customerType === 'comercio'
        ? await buildAdminNotificationWa({ ...formData, cuit: cleanCuit(formData.cuit), phone: cleanPhone(formData.phone) })
        : '';
      setPreviewWaLink(wa);
      setPreviewSubmitted(true);
      return;
    }

    setSaving(true);
    try {
      // ⚠️ IMPORTANTE: si el usuario elige "comercio", NO lo marcamos como
      // comercio en la DB todavía. Queda como 'consumidor_final' hasta que
      // el admin apruebe su solicitud en /admin/commerce-requests. Sin esta
      // guarda, el cliente quedaba auto-aprobado (bug de seguridad).
      //
      // 🔧 FIX 2026-09-04 pm-4: si el usuario elige comercio, NO llamamos
      // updateCustomerType porque:
      //   1. El backend con b2cFlow=false rechaza customer_type=consumidor_final
      //      con 403, causando que TODO el flujo se rompa silenciosamente.
      //   2. El user ya viene como consumidor_final por default. El estado
      //      objetivo (comercio) se materializa cuando el admin aprueba la
      //      solicitud desde /admin/commerce-requests.
      // Sólo llamamos updateCustomerType si el usuario elige consumidor_final
      // (por si el flag b2cFlow está encendido).
      if (formData.customerType !== 'comercio') {
        try {
          await updateCustomerType(formData.customerType);
        } catch (e) {
          const status = e?.response?.status;
          const msg = e?.response?.data?.detail || 'No pudimos actualizar tu tipo de cliente.';
          if (status === 403) {
            setErrorMsg(msg);
            setSaving(false);
            return;
          }
          throw e;
        }
      }
      // Guardar el perfil. Si falla, avisamos al usuario (antes se silenciaba).
      try {
        await axios.put(`${API}/user/profile`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          cuit: cleanCuit(formData.cuit),
          phone: cleanPhone(formData.phone),
          address: formData.address,
          addressNumber: formData.addressNumber,
          city: formData.city,
          province: formData.province,
          businessName: formData.businessName,
          condicionIva: formData.condicionIva,
          betweenStreets: formData.betweenStreets,
          googleMapsLink: formData.googleMapsLink,
          legacy_customer_id: linkedClient?.cliente_legacy_id || null,
        }, { withCredentials: true });
      } catch (e) {
        console.warn('user/profile update failed (no bloqueante):', e?.response?.data || e.message);
      }

      // Crear la solicitud de aprobación en `commerce_requests` para que el
      // admin la vea en el panel /admin/commerce-requests y pueda aprobar/
      // rechazar. Sólo aplica a COMERCIO — el Consumidor Final entra directo.
      // Si esta llamada falla, DEBEMOS avisar al user (antes se silenciaba).
      let commerceRequestOk = true;
      if (formData.customerType === 'comercio') {
        try {
          const condMap = {
            'Responsable Inscripto': 'responsable_inscripto',
            'Monotributo': 'monotributo',
            'Exento': 'exento',
          };
          await axios.post(`${API}/commerce-requests`, {
            business_name: formData.businessName,
            contact_name: `${formData.firstName} ${formData.lastName}`.trim(),
            whatsapp: cleanPhone(formData.phone),
            address: formData.address,
            address_number: formData.addressNumber,
            city: formData.city,
            cuit: cleanCuit(formData.cuit),
            condicion_iva: condMap[formData.condicionIva] || 'responsable_inscripto',
            notes: [
              formData.province ? `Provincia: ${formData.province}` : '',
              formData.betweenStreets ? `Entre calles: ${formData.betweenStreets}` : '',
              formData.googleMapsLink ? `Maps: ${formData.googleMapsLink}` : '',
            ].filter(Boolean).join(' | '),
          }, { withCredentials: true });
        } catch (err) {
          commerceRequestOk = false;
          const detail = err?.response?.data?.detail || err.message;
          console.error('commerce-requests POST failed:', detail, err?.response?.data);
          setErrorMsg(`Error al enviar la solicitud: ${detail}. Intente de nuevo.`);
          setSaving(false);
          return;
        }
      }

      const shippingData = {
        customerName: `${formData.firstName} ${formData.lastName}`.trim(),
        businessName: formData.businessName,
        phone: cleanPhone(formData.phone),
        cuit: cleanCuit(formData.cuit),
        condicionIva: formData.condicionIva,
        address: `${formData.address} ${formData.addressNumber}`.trim(),
        city: formData.city,
        province: formData.province,
        betweenStreets: formData.betweenStreets,
        googleMapsLink: formData.googleMapsLink,
      };
      localStorage.setItem('bebida-bacana-shipping', JSON.stringify(shippingData));
      localStorage.setItem('bebida-bacana-profile', JSON.stringify(formData));
      // Limpiar el intent que arrastramos desde el WelcomeGate — ya se
      // materializó en un customer_type real en la DB.
      try { localStorage.removeItem('bebida-bacana-login-intent'); } catch { /* storage */ }

      // Notificación al admin por WhatsApp cuando un COMERCIO nuevo se
      // registra. Abre wa.me en una pestaña nueva con un mensaje ya
      // pre-armado (razón social, CUIT, IVA, dirección, WhatsApp, etc.)
      // y link al panel para aprobación. Silenciar errores.
      if (formData.customerType === 'comercio') {
        try {
          const waLink = await buildAdminNotificationWa({
            ...formData,
            cuit: cleanCuit(formData.cuit),
            phone: cleanPhone(formData.phone),
          });
          if (waLink) window.open(waLink, '_blank');
        } catch { /* sin aviso, no bloqueamos el flujo */ }
      }

      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error saving registration:', error);
      const detail = error?.response?.data?.detail || error?.message || 'Error inesperado';
      setErrorMsg(`Error al enviar, intente de nuevo. (${detail})`);
    } finally { setSaving(false); }
  };

  const cuitValid = isValidCuit(formData.cuit);
  const phoneValid = isValidPhone(formData.phone);
  const isFormValid = formData.firstName.trim() && formData.lastName.trim()
    && phoneValid && formData.city.trim() && formData.address.trim()
    && formData.addressNumber.trim() && formData.province.trim()
    && (formData.customerType !== 'comercio' || (cuitValid && formData.businessName.trim() && formData.condicionIva));

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl my-8" data-testid="registration-form">
        {previewMode && (
          <div className="mb-3 -mt-2 -mx-2 px-3 py-2 rounded-lg bg-amber-100 border border-amber-300 flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-bold text-amber-800">Vista previa (Admin)</p>
              <p className="text-[10px] text-amber-700 leading-tight">Simulación — nada se guardará.</p>
            </div>
            {onComplete && (
              <button type="button" onClick={onComplete}
                className="text-xs font-bold text-amber-900 hover:bg-amber-200 px-2 py-1 rounded"
                data-testid="preview-close-btn">
                Cerrar
              </button>
            )}
          </div>
        )}
        {previewMode && previewSubmitted && (
          <div className="mb-3 p-3 rounded-lg bg-emerald-50 border border-emerald-300 space-y-2">
            <p className="text-xs font-bold text-emerald-800">✓ Envío simulado con éxito</p>
            <p className="text-[10px] text-emerald-700 leading-tight">
              En un flujo real, ahora el comercio quedaría con customer_type=&quot;comercio&quot; y
              sus datos guardados en el perfil (nombre, CUIT, dirección, teléfono, IVA). Al
              hacer un pedido, el checkout se auto-completaría con estos datos.
            </p>
            {previewWaLink && (
              <div className="pt-2 border-t border-emerald-200">
                <p className="text-[10px] text-emerald-700 mb-1.5 leading-tight">
                  <b>Mensaje que se enviaría por WhatsApp al admin</b> en producción.
                  Podés abrirlo ahora para probar cómo llega.
                </p>
                <a
                  href={previewWaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors"
                  data-testid="preview-open-wa-btn"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Abrir WhatsApp con el aviso al admin
                </a>
              </div>
            )}
          </div>
        )}
        <div className="text-center mb-4">
          <img src="/logo.png" alt="Bebida Bacana" className="h-16 mx-auto mb-2" onError={(e) => e.target.style.display = 'none'} />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed', color: theme.text }}>Completá tu registro</h2>
          <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>Datos obligatorios para habilitar tu cuenta</p>
        </div>

        <div className="mb-3 px-3 py-2 rounded-lg bg-sky-50 border border-sky-200 flex items-start gap-2">
          <AlertCircle size={14} className="text-sky-600 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-sky-800 leading-tight">
            <b>Sólo te vamos a pedir estos datos una vez.</b> Después, cada vez que hagas un pedido,
            el checkout se completa automáticamente. Si algo cambia, podés editarlo desde Mi Cuenta.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: theme.text }}>Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setFormData(prev => ({ ...prev, customerType: 'consumidor_final' }))}
                className={`p-2.5 rounded-xl border-2 text-left ${formData.customerType === 'consumidor_final' ? 'border-slate-700 bg-slate-50' : 'border-slate-200'}`} data-testid="reg-type-particular">
                <User size={18} className="mb-1 text-slate-600" />
                <p className="font-bold text-sm">Particular</p>
              </button>
              <button type="button" onClick={() => setFormData(prev => ({ ...prev, customerType: 'comercio' }))}
                className={`p-2.5 rounded-xl border-2 text-left ${formData.customerType === 'comercio' ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`} data-testid="reg-type-comercio">
                <Building2 size={18} className="mb-1" style={{ color: theme.primary }} />
                <p className="font-bold text-sm">Comercio</p>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>Nombre <span className="text-rose-600">*</span></label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errBorder('firstName')}`} placeholder="Juan" data-testid="reg-firstName" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>Apellido <span className="text-rose-600">*</span></label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errBorder('lastName')}`} placeholder="Pérez" data-testid="reg-lastName" />
            </div>
          </div>

          {/* CUIT — sólo obligatorio para comercios. Particulares no lo necesitan. */}
          {formData.customerType === 'comercio' && (
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
              CUIT / CUIL <span className="text-rose-600">*</span>
              <span className="text-[10px] text-slate-400 ml-1">(11 dígitos, sin guiones)</span>
            </label>
            <div className="relative">
              <FileText size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" inputMode="numeric" name="cuit" value={formData.cuit}
                onChange={handleCuitChange} maxLength="11"
                className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm font-mono ${
                  formData.cuit.length === 0 ? errBorder('cuit')
                  : cuitValid ? 'border-emerald-500 bg-emerald-50'
                  : 'border-amber-400 bg-amber-50'
                }`}
                placeholder="20123456789" data-testid="reg-cuit" />
            </div>
            {formData.cuit.length > 0 && !cuitValid && (
              <p className="text-[11px] text-amber-700 mt-0.5">CUIT inválido. Debe tener 11 dígitos con dígito verificador correcto.</p>
            )}
            {linkedClient && (
              <p className="text-[11px] text-emerald-700 mt-0.5 font-bold">✓ Cuenta encontrada en nuestro sistema: {linkedClient.razon_social}. Se vinculará tu historial.</p>
            )}
          </div>
          )}

          {formData.customerType === 'comercio' && (
            <>
              <input type="text" name="businessName" value={formData.businessName} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${errBorder('businessName')}`} placeholder="Nombre del Comercio / Razón Social" data-testid="reg-businessName" />
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>Condición IVA <span className="text-rose-600">*</span></label>
                <select name="condicionIva" value={formData.condicionIva} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${errBorder('condicionIva')}`} data-testid="reg-iva">
                  <option>Responsable Inscripto</option>
                  <option>Monotributo</option>
                  <option>Exento</option>
                </select>
              </div>
            </>
          )}

          {formData.customerType === 'consumidor_final' && minOrder > 0 && (
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Aviso:</strong> tenemos un mínimo de compra de <strong>${minOrder.toLocaleString('es-AR')}</strong>. Pedidos menores no pueden procesarse.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
              WhatsApp <span className="text-rose-600">*</span>
            </label>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm ${
                  formData.phone.length === 0 ? errBorder('phone')
                  : phoneValid ? 'border-emerald-500 bg-emerald-50'
                  : 'border-amber-400 bg-amber-50'
                }`}
                placeholder="3571 123456" data-testid="reg-phone" />
            </div>
            {formData.phone.length > 0 && !phoneValid && (
              <p className="text-[11px] text-amber-700 mt-0.5">Mínimo 8 dígitos.</p>
            )}
          </div>

          <div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                  Calle <span className="text-rose-600">*</span>
                </label>
                <div className="relative">
                  <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" name="address" value={formData.address} onChange={handleChange}
                    className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm ${errBorder('address')}`} placeholder="Av. San Martín" data-testid="reg-address" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>
                  N° <span className="text-rose-600">*</span>
                </label>
                <input type="text" inputMode="numeric" name="addressNumber" value={formData.addressNumber} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${errBorder('addressNumber')}`} placeholder="1234" data-testid="reg-address-number" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>Ciudad <span className="text-rose-600">*</span></label>
              {cities.length > 0 ? (
                <select
                  name="city"
                  value={cities.includes(formData.city) ? formData.city : (formData.city ? '__OTRA__' : '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__OTRA__') {
                      setFormData(prev => ({ ...prev, city: prev.city && !cities.includes(prev.city) ? prev.city : '' }));
                    } else {
                      setFormData(prev => ({ ...prev, city: v }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${errBorder('city')}`}
                  data-testid="reg-city"
                >
                  <option value="">— Elegí tu ciudad —</option>
                  <optgroup label="Ciudades con reparto">
                    {cities.map(c => <option key={c} value={c}>{c}</option>)}
                  </optgroup>
                  <option value="__OTRA__">Otra ciudad (retiro por local)</option>
                </select>
              ) : (
                <input type="text" name="city" value={formData.city} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg text-sm ${errBorder('city')}`} placeholder="Río Tercero" data-testid="reg-city" />
              )}
              {cities.length > 0 && !cities.includes(formData.city) && (
                <input
                  type="text"
                  autoFocus
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={`w-full mt-1 px-3 py-2 border rounded-lg text-sm ${errBorder('city')}`}
                  placeholder="Escribí tu ciudad"
                  data-testid="reg-city-other"
                />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: theme.text }}>Provincia <span className="text-rose-600">*</span></label>
              <select name="province" value={formData.province} onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white ${errBorder('province')}`} data-testid="reg-province">
                {['Córdoba','Buenos Aires','CABA','Catamarca','Chaco','Chubut','Corrientes','Entre Ríos','Formosa','Jujuy','La Pampa','La Rioja','Mendoza','Misiones','Neuquén','Río Negro','Salta','San Juan','San Luis','Santa Cruz','Santa Fe','Santiago del Estero','Tierra del Fuego','Tucumán'].map(p =>
                  <option key={p} value={p}>{p}</option>
                )}
              </select>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 -mt-2">Reparto disponible sólo en las ciudades del listado. Si vivís en otra, podés retirar por el local.</p>

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500 font-medium hover:text-slate-700">📍 Ubicación complicada? (opcional)</summary>
            <div className="mt-2 space-y-2 pl-2 border-l-2 border-slate-100">
              <div>
                <label className="block text-[11px] font-medium mb-1 text-slate-600">Entre calles</label>
                <input type="text" name="betweenStreets" value={formData.betweenStreets} onChange={handleChange}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
                  placeholder="Ej: entre Mitre y Belgrano" data-testid="reg-between-streets" />
              </div>
              <div>
                <label className="block text-[11px] font-medium mb-1 text-slate-600">Link Google Maps</label>
                <input type="url" name="googleMapsLink" value={formData.googleMapsLink} onChange={handleChange}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-mono"
                  placeholder="https://maps.app.goo.gl/..." data-testid="reg-maps-link" />
                <p className="text-[10px] text-slate-400 mt-0.5">Abrí Google Maps → marcá tu ubicación → &quot;Compartir&quot; → pegá el link acá.</p>
              </div>
            </div>
          </details>

          {attempted && errorMsg && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-300 flex items-start gap-2" data-testid="form-error-summary">
              <AlertCircle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-rose-800">Faltan datos por completar</p>
                <p className="text-[11px] text-rose-700 leading-snug mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          <button type="submit" disabled={saving}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-bold disabled:opacity-40"
            style={{ backgroundColor: theme.primary }} data-testid="reg-submit-btn">
            {saving ? <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Guardando...</> :
              <><Save size={16} />Confirmar y comenzar</>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
